import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { size, map, get, sumBy } from 'lodash/fp';
import PropTypes from 'prop-types';
import { Tag, Whisper, Icon, Button } from 'rsuite';
import FoodStoplight from '../../FoodStoplight';
import {
  InventoryItemType,
  InventoryPackageType,
  InventoryAvailabilityType,
  InventoryItemSkuType,
  IventoryPackageItemType
} from '../../../interfaces/inventory';
import { hasFullAccess } from '../../../helpers/permission';
import * as PERMISSIONS from '../../../constants/permissions/inventory';
import { getUserPermissionsAsMap } from '../../../redux/selectors/permission';
import InventoryImage from '../Image';
import AvailabilityPopover from './AvailabilityPopover';
import PackageItemsPreview from '../PackageItemsPreview';
import PackageItemsPopover from '../PackageItemsPopover';
import ItemSkus from '../../../containers/inventory/ItemSkus';
import { ItemType } from '../../../constants/inventory/InventoryTypes';

export const ItemGroup = styled.div`
  max-width: 400px;
  padding: 3px 0;
`;

export const ItemLabel = styled.label`
  display: inline-block;
  text-transform: none;
  font-size: 12px;
  margin-right: 30px;
  width: 100px;
  font-weight: normal;
  color: #8e8e93;
`;

const SubLabel = styled(ItemLabel)`
  color: '#8e8e93';
  width: auto;
`;

const ItemName = styled.h4`
  margin: 0;
`;

const Price = styled.strong`
  font-size: 16px;
  margin-right: 10px;
`;

export const ItemValue = styled.div`
  display: inline-block;
  text-align: left;
  text-transform: none;
  font-size: 13px;
  font-weight: normal;
`;

const ImageWrapper = styled.div`
  position: relative;
  display: inline-block;
  border: 1px solid;
  border-color: #adadad;
  padding: 3px;
`;

const Section = styled.div`
  display: inline-block;
  vertical-align: top;
`;

const InventoryItemDetails = ({ item, skus, packageItems, availability }) => {
  const permissions = useSelector(getUserPermissionsAsMap);
  const isItemType = get('itemType', item) === ItemType;

  return (
    <div>
      <ImageWrapper style={{ marginRight: 20 }}>
        <InventoryImage
          alt="photo"
          image={item.photo}
          style={{ borderRadius: 5 }}
        />
      </ImageWrapper>
      <Section key="details">
        <ItemName>{item.name}</ItemName>
        {item.stoplight > 0 && (
          <ItemGroup key="stoplight" style={{ height: '25px' }}>
            <FoodStoplight value={item.stoplight} />
          </ItemGroup>
        )}
        <ItemGroup key="price" style={{ margin: '10px 0' }}>
          <ItemValue>
            <Price>${item.price}</Price>
            <SubLabel key="weight">
              {parseFloat(item.weight)}
              lb
            </SubLabel>
          </ItemValue>
        </ItemGroup>
        {item.description && (
          <ItemGroup key="desc" style={{ margin: '10px 0' }}>
            <ItemValue>{item.description}</ItemValue>
          </ItemGroup>
        )}
        <ItemGroup key="qty">
          <ItemLabel>Qty Available</ItemLabel>
          <ItemValue>{sumBy('quantity', availability)}</ItemValue>
        </ItemGroup>

        <ItemGroup key="points">
          <ItemLabel>Points</ItemLabel>
          <ItemValue>{item.points}</ItemValue>
        </ItemGroup>
        {isItemType && (
          <ItemGroup key="minCount">
            <ItemLabel>Min Count</ItemLabel>
            <ItemValue>{item.minCount}</ItemValue>
          </ItemGroup>
        )}
        {isItemType && (
          <ItemGroup key="maxCount">
            <ItemLabel>Max Count</ItemLabel>
            <ItemValue>{item.maxCount}</ItemValue>
          </ItemGroup>
        )}
        {size(skus) > 0 && (
          <ItemGroup key="skus">
            <ItemLabel>SKU(s)</ItemLabel>
            <ItemValue>
              <ItemSkus
                skus={skus}
                type={item.itemType}
                hasFullAccess={hasFullAccess(PERMISSIONS.SKU, permissions)}
              />
            </ItemValue>
          </ItemGroup>
        )}
        {size(item.tags) > 0 && (
          <ItemGroup key="tags">
            <ItemLabel>Tags</ItemLabel>
            <ItemValue>
              {map(
                ({ name }) => (
                  <Tag key={name}>{name}</Tag>
                ),
                item.tags
              )}
            </ItemValue>
          </ItemGroup>
        )}
        {size(availability) > 0 && (
          <ItemGroup>
            <ItemLabel>Availability</ItemLabel>
            <ItemValue>
              {get('0.location.name', availability)} {' ...'}
            </ItemValue>
            <Whisper
              placement="right"
              trigger="click"
              speaker={<AvailabilityPopover availability={availability} />}
            >
              <Button size="xs" appearance="link">
                <Icon icon="external-link-square" />
              </Button>
            </Whisper>
          </ItemGroup>
        )}
        {size(packageItems) > 0 && (
          <ItemGroup>
            <ItemLabel>Items</ItemLabel>
            <ItemValue>
              <PackageItemsPreview packageItems={packageItems} />
            </ItemValue>
            <Whisper
              placement="right"
              trigger="click"
              speaker={<PackageItemsPopover packageItems={packageItems} />}
            >
              <Button size="xs" appearance="link">
                <Icon icon="external-link-square" />
              </Button>
            </Whisper>
          </ItemGroup>
        )}
      </Section>
    </div>
  );
};

InventoryItemDetails.propTypes = {
  item: PropTypes.oneOfType([InventoryItemType, InventoryPackageType])
    .isRequired,
  skus: PropTypes.arrayOf(InventoryItemSkuType),
  packageItems: PropTypes.arrayOf(IventoryPackageItemType),
  availability: PropTypes.arrayOf(InventoryAvailabilityType)
};

InventoryItemDetails.defaultProps = {
  skus: [],
  packageItems: [],
  availability: []
};

export default InventoryItemDetails;
